import React, { useEffect, useState } from 'react';
import { MockAttempt, MockPaperCard, packagesAPI, track } from '../../services/api';
import './PackageMocks.css';

/**
 * The mock room — the papers a student can sit, and how they have done.
 *
 * ## Why this is a room and not two buttons
 *
 * It used to be two buttons above the lesson grid labelled "🧮 Quantitative
 * Mock" and "📝 Verbal & Analytical Mock". Three things were wrong with that:
 *
 *   1. They pushed thirty-eight lesson cards down the page to advertise
 *      something a student does twice, not daily.
 *   2. They did not open a mock. They opened a 25-minute check sampled from
 *      the practice pool — useful, free, and NOT a paper, but wearing a
 *      paper's name.
 *   3. The four authored papers — 200 items, written to a blueprint, validated
 *      item by item — had nowhere to appear at all.
 *
 * So the checks keep their job under an honest name, and the papers get the
 * room. What a student decides here is "which paper, and am I ready" — that is
 * a screen, not a button.
 *
 * ## Two forms, and why the second one is visible but shut
 *
 * Each product has parallel forms A and B. A student is assigned one by a
 * stable split on their account id and meets the other as the re-test after
 * review. The re-test is SHOWN rather than hidden: a student who can see a
 * second paper waiting paces the first one differently, and hiding it made the
 * room look like a two-item list that was finished by Tuesday. It stays shut
 * until its partner has actually been sat, because handing over both parallel
 * papers at once is how a student burns the measure of their own progress on a
 * single afternoon.
 */

type Props = {
  pkg: string;
  /** Open a paper. The room does not run it — `PackageSitting` does. */
  onOpen: (formCode: string) => void;
  /** The free pool-sampled check, which lives here now under its own name. */
  onQuickCheck: (subject: string, label: string) => void;
  quickCheckBusy: string | null;
};

/** A free pool-sampled check, as the package declares it. */
type QuickCheck = { subject: string; label: string; icon?: string };

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : '';

/** What a card says about itself, in one line, under the title. */
const statusLine = (p: MockPaperCard): string => {
  if (p.locked && p.locked_reason === 'access') return 'Part of the full package';
  if (p.locked && p.locked_reason === 'retest')
    return 'Opens after you sit the first paper';
  if (p.status === 'done' && p.attempt)
    return `Sat ${when(p.attempt.started_at)} · ${p.attempt.correct}/${p.attempt.total}`;
  if (p.status === 'started') return 'Started — pick up where you left off';
  return 'Not started';
};

const PaperCard: React.FC<{ p: MockPaperCard; onOpen: () => void }> = ({ p, onOpen }) => (
  <li
    className="pm-card"
    data-locked={p.locked || undefined}
    data-done={p.status === 'done' || undefined}
  >
    <div className="pm-card__icon" aria-hidden>
      {p.locked ? '🔒' : p.icon}
    </div>

    <div className="pm-card__body">
      <div className="pm-card__title">
        {p.label}
        {/* The form letter is part of the paper's identity — a student
            comparing two scores needs to know they were different papers —
            but it is not the headline, so it rides as a quiet chip. */}
        <span className="pm-form">Form {p.letter}</span>
        {p.is_retest && <span className="pm-retest">Re-test</span>}
      </div>

      <div className="pm-card__meta">
        {p.count} questions · {p.minutes} min
      </div>

      <div className="pm-card__status" data-tone={p.status}>
        {statusLine(p)}
      </div>

      {/* A finished paper keeps its score on the card. The number IS the
          reason to come back, and burying it one tap down inside a results
          screen made the room look identical before and after an attempt. */}
      {p.status === 'done' && p.attempt?.score_pct != null && (
        <div className="pm-score">
          <div className="pm-score__bar">
            <i style={{ width: `${p.attempt.score_pct}%` }} />
          </div>
          <span className="pm-score__pct">{p.attempt.score_pct}%</span>
        </div>
      )}
    </div>

    <button
      className="pm-card__go"
      onClick={onOpen}
      disabled={p.locked}
      aria-label={`${p.locked ? 'Locked: ' : ''}${p.label}, Form ${p.letter}`}
    >
      {p.locked
        ? p.locked_reason === 'access'
          ? 'Unlock'
          : 'Locked'
        : p.status === 'done'
          ? 'Retake'
          : p.status === 'started'
            ? 'Resume'
            : 'Start'}
    </button>
  </li>
);

const PackageMocks: React.FC<Props> = ({
  pkg,
  onOpen,
  onQuickCheck,
  quickCheckBusy,
}) => {
  const [papers, setPapers] = useState<MockPaperCard[] | null>(null);
  const [attempts, setAttempts] = useState<MockAttempt[]>([]);
  const [access, setAccess] = useState<'free' | 'full'>('free');
  const [checks, setChecks] = useState<QuickCheck[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await packagesAPI.mocks(pkg);
        if (dead) return;
        setPapers(r.papers || []);
        setAttempts(r.attempts || []);
        setAccess(r.access || 'free');
        setChecks(r.checks || []);
      } catch (e: any) {
        if (!dead) setErr('Could not load the mock papers.');
      }
    })();
    return () => {
      dead = true;
    };
  }, [pkg]);

  // Attempts that belong to no paper: the pool-sampled checks, and anything
  // recorded before the papers existed. Kept in their own list rather than
  // mixed into the cards, because a check and a paper are not comparable and
  // showing them in one column invites exactly that comparison.
  const looseAttempts = attempts.filter((a) => !a.form_code);

  if (err)
    return (
      <div className="pm-empty">
        <h3>Mocks are unavailable</h3>
        <p className="ps-muted">{err}</p>
      </div>
    );

  return (
    <div className="pm">
      <div className="pm-lead">
        <h2 className="pm-h">Mock exams</h2>
        <p className="ps-muted">
          A full paper under a clock. Nothing is revealed — no answers, no
          explanations, no Ask Lomi — until you submit. Then everything is.
        </p>
      </div>

      {papers === null ? (
        <ul className="pm-list" aria-busy="true">
          {[0, 1].map((i) => (
            <li key={i} className="pm-card pm-card--skeleton" />
          ))}
        </ul>
      ) : papers.length === 0 ? (
        <div className="pm-empty">
          <h3>No papers published yet</h3>
          {/* The second sentence pointed at a quick check that is no longer
              always there — a package with none declared shows no check block,
              so the copy promised something the screen did not have. */}
          <p className="ps-muted">
            The mock papers for this package have not been imported.
            {checks.length > 0 && ' The quick check below still works.'}
          </p>
        </div>
      ) : (
        <ul className="pm-list">
          {papers.map((p) => (
            <PaperCard
              key={p.form_code}
              p={p}
              onOpen={() => {
                track('package.mock.open', {
                  package: pkg,
                  form: p.form_code,
                  locked: p.locked,
                  status: p.status,
                });
                onOpen(p.form_code);
              }}
            />
          ))}
        </ul>
      )}

      {/* ── The quick check ────────────────────────────────────────────────
          Moved here from the top of the lesson list, and renamed. It samples
          the practice pool, so it is a read of where a student stands and not
          a paper — calling it a "mock" beside four authored papers would make
          two different things share one word in one screen. Free, and staying
          free after purchase: a paid student still needs a 25-minute option. */}
      {/* The checks the PACKAGE declares. These two buttons were hardcoded to
          UAT's, so an EAU student was offered a "Quantitative" and a "Verbal &
          Analytical" check belonging to a different exam and sampling a pool
          EAU does not have. A package with none declared shows no block. */}
      {checks.length > 0 && (
        <div className="pm-quick">
          <div className="pm-quick__head">
            <h3 className="pm-quick__h">Quick check</h3>
            <span className="pm-quick__free">Free</span>
          </div>
          <p className="ps-muted pm-quick__blurb">
            About 25 minutes, drawn from the practice bank. Not a scored paper —
            a read of where you stand, by topic.
          </p>
          <div className="pm-quick__row">
            {checks.map((c) => (
              <button
                key={c.subject}
                className="pm-quick__btn"
                disabled={!!quickCheckBusy}
                onClick={() => onQuickCheck(c.subject, c.label)}
              >
                {c.icon ? `${c.icon} ` : ''}
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {looseAttempts.length > 0 && (
        <div className="pm-history">
          <h3 className="pm-quick__h">Earlier checks</h3>
          <ul className="pm-attempts">
            {looseAttempts.slice(0, 8).map((a) => (
              <li key={a.session_id} className="pm-att">
                <span className="pm-att__when">{when(a.started_at) || '—'}</span>
                <span className="pm-att__score">
                  <b>{a.correct}</b>
                  <span>/{a.total}</span>
                </span>
                <span className="pm-att__bar">
                  <i style={{ width: `${a.score_pct ?? 0}%` }} />
                </span>
                <span className="pm-att__pct">
                  {a.score_pct != null ? `${a.score_pct}%` : '—'}
                  {!a.finished && <em> · left early</em>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {access === 'free' && (
        <p className="ps-muted pm-foot">
          The four mock papers come with the full package. The quick check is
          free and always will be.
        </p>
      )}
    </div>
  );
};

export default PackageMocks;
